/**
 * Notification Helper
 * Bildirim oluşturma yardımcı fonksiyonları
 */

import { base44 } from '@/api/base44Client';

/**
 * Bildirim oluşturur
 * @param {Object} params
 * @param {string} params.recipientEmail - Alıcı email
 * @param {string} params.senderEmail - Gönderen email
 * @param {string} params.senderName - Gönderen isim
 * @param {string} params.type - Bildirim tipi
 * @param {string} params.title - Başlık
 * @param {string} params.message - Mesaj
 * @param {string} params.referenceType - Referans tipi (task/order/lead/offer)
 * @param {string} params.referenceId - Referans ID
 */
export async function createNotification({
  recipientEmail,
  senderEmail,
  senderName,
  type,
  title,
  message,
  referenceType,
  referenceId,
}) {
  if (!recipientEmail || !title) return null;

  try {
    return await base44.entities.Notification.create({
      recipient_email: recipientEmail,
      sender_email: senderEmail || '',
      sender_name: senderName || '',
      type: type || 'system',
      title,
      message: message || '',
      reference_type: referenceType || '',
      reference_id: referenceId || '',
      is_read: false,
    });
  } catch (error) {
    console.warn('Notification creation failed:', error);
    return null;
  }
}

/**
 * Admin email adresini getirir
 */
export async function getAdminEmail() {
  try {
    const agents = await base44.entities.Agent.list();
    const admin = agents.find(a => (a.rolle || '').toLowerCase() === 'admin');
    return admin?.email || 'admin@inmotion.de';
  } catch {
    return 'admin@inmotion.de';
  }
}

/**
 * Görev tamamlandığında admin'e bildirim
 */
export async function notifyTaskCompleted(task, agentEmail, agentName, extra = {}) {
  const adminEmail = await getAdminEmail();
  // Detay: yalnızca başlık/durum değil, görevde NE olduğu da görünsün
  const ergebnis = extra.ergebnis || task.ergebnis || null;
  const notes = extra.notes || null;
  const details = [
    ergebnis ? `Ergebnis: ${ergebnis}` : null,
    notes && notes !== ergebnis ? `Notiz: ${notes}` : null,
  ].filter(Boolean).join(' · ');

  return createNotification({
    recipientEmail: adminEmail,
    senderEmail: agentEmail,
    senderName: agentName,
    type: 'task_completed',
    title: `Aufgabe abgeschlossen: ${task.task_type || task.typ}`,
    message: `${agentName || agentEmail} hat die Aufgabe "${task.task_type || task.typ}" für ${task.lead_name || 'unbekannt'} abgeschlossen.${details ? ` — ${details}` : ''}`,
    referenceType: 'task',
    referenceId: task.id,
  });
}

/**
 * Yeni görev atandığında agent'a bildirim
 */
export async function notifyTaskAssigned(task, assignedAgentEmail) {
  return createNotification({
    recipientEmail: assignedAgentEmail,
    type: 'task_assigned',
    title: `Neue Aufgabe: ${task.task_type}`,
    message: `Neue Aufgabe "${task.task_type}" für ${task.lead_name || 'unbekannt'} am ${task.due_date || 'ohne Datum'}.`,
    referenceType: 'task',
    referenceId: task.id,
  });
}

/**
 * PDF oluşturulduğunda agent'a bildirim
 */
export async function notifyPdfCreated(order, agentEmail) {
  return createNotification({
    recipientEmail: agentEmail,
    type: 'pdf_created',
    title: `PDF erstellt: ${order.order_number}`,
    message: `Das PDF für ${order.order_type} ${order.order_number} (${order.lead_name}) wurde erstellt und genehmigt.`,
    referenceType: 'order',
    referenceId: order.id,
  });
}

/**
 * Yeni lead oluşturulduğunda admin'e bildirim
 */
export async function notifyNewLead(lead, agentEmail, agentName) {
  const adminEmail = await getAdminEmail();
  return createNotification({
    recipientEmail: adminEmail,
    senderEmail: agentEmail,
    senderName: agentName,
    type: 'lead_assigned',
    title: `Neuer Kontakt: ${lead.name}`,
    message: `${agentName || agentEmail} hat den Kontakt "${lead.name}" (${lead.branche || ''}) erstellt.`,
    referenceType: 'lead',
    referenceId: lead.id,
  });
}

/**
 * Toplu lead import bildirim
 */
export async function notifyBulkLeadImport(count, agentEmail, agentName) {
  const adminEmail = await getAdminEmail();
  return createNotification({
    recipientEmail: adminEmail,
    senderEmail: agentEmail,
    senderName: agentName,
    type: 'lead_assigned',
    title: `${count} Kontakte importiert`,
    message: `${agentName || agentEmail} hat ${count} neue Kontakte über AI Scout importiert.`,
    referenceType: 'lead',
  });
}

/**
 * Teklif/sipariş oluşturulduğunda admin'e bildirim
 */
export async function notifyOrderCreated(order, agentEmail, agentName) {
  const adminEmail = await getAdminEmail();
  const typeLabel = order.order_type === 'Angebot' ? 'Angebot' : 'Bestellung';
  return createNotification({
    recipientEmail: adminEmail,
    senderEmail: agentEmail,
    senderName: agentName,
    type: 'order_created',
    title: `Neues ${typeLabel}: ${order.order_number}`,
    message: `${agentName || agentEmail} hat ein ${typeLabel} für ${order.lead_name} erstellt (€${order.total?.toFixed(2)}).`,
    referenceType: 'order',
    referenceId: order.id,
  });
}

/**
 * Admin lead oluşturduğunda atanan agent'a bildirim
 */
export async function notifyLeadAssignedToAgent(lead, agentEmail) {
  if (!agentEmail) return null;
  return createNotification({
    recipientEmail: agentEmail,
    type: 'lead_assigned',
    title: `Neuer Kontakt zugewiesen: ${lead.name}`,
    message: `Ihnen wurde der Kontakt "${lead.name}" (${lead.branche || ''}, ${lead.ort || ''}) zugewiesen.`,
    referenceType: 'lead',
    referenceId: lead.id,
  });
}

/**
 * Gelen teklif agent'a atandığında bildirim
 */
export async function notifyOfferAssigned(offer, agentEmail) {
  return createNotification({
    recipientEmail: agentEmail,
    type: 'offer_sent',
    title: `Neues Angebot zugewiesen: ${offer.subject}`,
    message: `Ihnen wurde ein Angebot von "${offer.customer_name}" zugewiesen.`,
    referenceType: 'offer',
    referenceId: offer.id,
  });
}
